import React, { useEffect, useRef, useState } from 'react';

import { DEFAULT_WIDTH, MAX_WIDTH, MIN_WIDTH, tabs } from '../../constants';
import { companyDataMap, defaultData } from '../../data';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useLiveMarketData } from '../../hooks/useLiveMarketData';
import { useResizablePane } from '../../hooks/useResizablePane';
import { colors } from '../../theme';
import type { FinancialDataPaneProps } from '../../types';
import { MarketDataFooter } from './MarketDataFooter';
import { PaneHeader } from './PaneHeader';

export function FinancialDataPane({
  onSourceLinkClick,
  onClose,
  currentXbrlTag,
  ticker,
  onFilingTypeChange,
  onFilingYearChange,
}: FinancialDataPaneProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState('summary');
  const [filingSelection, setFilingSelection] = useState({
    ticker,
    index: 0,
  });
  const [showFilingDropdown, setShowFilingDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedFilingIndex =
    filingSelection.ticker === ticker ? filingSelection.index : 0;
  const setSelectedFilingIndex: React.Dispatch<
    React.SetStateAction<number>
  > = update => {
    setFilingSelection(previousSelection => {
      const previousIndex =
        previousSelection.ticker === ticker ? previousSelection.index : 0;
      const nextIndex =
        typeof update === 'function' ? update(previousIndex) : update;
      return {
        ticker,
        index: nextIndex,
      };
    });
  };

  const { width, handleMouseDown } = useResizablePane({
    defaultWidth: DEFAULT_WIDTH,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
  });
  useClickOutside(dropdownRef, () => setShowFilingDropdown(false));

  // Get company data based on ticker
  const companyData = companyDataMap[ticker.toUpperCase()] || defaultData;
  const currentFiling =
    companyData.filings[selectedFilingIndex] || companyData.filings[0];

  // Notify parent of filing type and year changes
  useEffect(() => {
    if (onFilingTypeChange) {
      onFilingTypeChange(currentFiling.filingType);
    }
    if (onFilingYearChange) {
      // Extract year from periodEnd (e.g., "Jan 31, 2025" -> "2025")
      const yearMatch = currentFiling.periodEnd.match(/\d{4}/);
      onFilingYearChange(yearMatch ? yearMatch[0] : '');
    }
  }, [
    currentFiling.filingType,
    currentFiling.periodEnd,
    onFilingTypeChange,
    onFilingYearChange,
  ]);

  const liveData = useLiveMarketData(ticker);

  // Get net debt for EV calculation
  // Always use first filing (most recent) so live data stays consistent across tabs
  const getNetDebt = (): number => {
    const mostRecentFiling = companyData.filings[0];
    const netDebtRow = mostRecentFiling.rows.find(r => r.label === 'Net debt');
    if (netDebtRow) {
      // Handle parentheses for negative numbers
      const value = netDebtRow.col1.replace(/,/g, '').replace(/[()]/g, '');
      const num = parseFloat(value) || 0;
      return netDebtRow.col1.includes('(') ? -num : num;
    }
    return 0;
  };

  const formatChange = (change: number | null): React.ReactElement | null => {
    if (change === null) return null;
    const isPositive = change >= 0;
    return (
      <span
        className='text-[10px] font-medium'
        style={{
          color: isPositive ? colors.positive : colors.negative,
        }}
      >
        {isPositive ? '+' : ''}
        {change.toFixed(1)}%
      </span>
    );
  };

  /** Build an XBRL source URL for a cell click and navigate to it */
  const handleCellClick = (
    xbrlTag: string,
    periodEndDate: string,
    colKey: 'col1' | 'col2' | 'col3'
  ): void => {
    if (!onSourceLinkClick) return;
    const filingPath =
      currentFiling.filingPathOverrides?.[colKey] ?? currentFiling.filingPath;
    const params = new URLSearchParams();
    params.set('f', filingPath);
    params.set('tag', xbrlTag);
    params.set('end', periodEndDate);
    onSourceLinkClick(`https://source.meridian.ai?${params.toString()}`);
  };

  /** Resolve the effective filing path for a given column */
  const getColFilingPath = (
    colKey: 'col1' | 'col2' | 'col3'
  ): string | undefined =>
    currentFiling.filingPathOverrides?.[colKey] ?? currentFiling.filingPath;

  /**
   * Whether a cell value should render as a clickable source link.
   * A filing path override of '' explicitly disables links for that column
   * (used for derived values like Q4 standalone that have no XBRL source).
   */
  const isLinkable = (
    value: string,
    xbrlTag?: string,
    colKey?: 'col1' | 'col2' | 'col3'
  ): boolean =>
    !!xbrlTag &&
    !!value &&
    value !== '-' &&
    value !== '—' &&
    value !== '' &&
    (colKey ? getColFilingPath(colKey) !== '' : true);

  return (
    <div
      className='h-full flex flex-col relative overflow-hidden bg-white border-r border-edge'
      style={{
        width: `${width}px`,
        minWidth: `${MIN_WIDTH}px`,
        maxWidth: `${MAX_WIDTH}px`,
      }}
    >
      {/* Resize handle */}
      <div
        onMouseDown={handleMouseDown}
        className='absolute top-0 right-0 h-full w-1 cursor-col-resize z-10 group bg-transparent'
      >
        <div className='absolute top-0 right-0 h-full w-1 transition-colors group-hover:bg-blue-400 bg-transparent' />
      </div>

      <PaneHeader
        companyData={companyData}
        currentFiling={currentFiling}
        selectedFilingIndex={selectedFilingIndex}
        showFilingDropdown={showFilingDropdown}
        setShowFilingDropdown={setShowFilingDropdown}
        setSelectedFilingIndex={setSelectedFilingIndex}
        dropdownRef={dropdownRef}
        onClose={onClose}
      />

      {/* Tabs - more compact */}
      <div className='shrink-0 flex items-center justify-between border-b border-edge bg-surface'>
        <div
          className='flex overflow-x-auto overflow-y-hidden scrollbar-thin'
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--color-edge) transparent',
          }}
        >
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className='px-2.5 py-1.5 whitespace-nowrap transition-colors text-[11px] -mb-px'
              style={{
                fontWeight: activeTab === tab.id ? 600 : 500,
                color: activeTab === tab.id ? colors.navy : colors.inkMuted,
                background: activeTab === tab.id ? '#fff' : 'transparent',
                borderBottom:
                  activeTab === tab.id
                    ? `2px solid ${colors.navy}`
                    : '2px solid transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className='text-[10px] text-ink-muted pr-2 whitespace-nowrap'>
          ($ in{' '}
          {companyData.valueScale === 'millions' ? 'millions' : 'thousands'})
        </div>
      </div>

      {/* Content */}
      <div className='flex-1 overflow-auto'>
        {activeTab === 'summary' ? (
          <div className='text-xs'>
            {/* Column headers - different for 10-K vs 10-Q */}
            {currentFiling.filingType === '10-K' ? (
              // 10-K: 2 data columns + YoY
              <div className='grid sticky top-0 z-10 grid-cols-[1fr_80px_80px_58px] gap-x-2 bg-surface border-b border-edge px-2 py-1.5 text-[10px] font-semibold text-ink-muted'>
                <div>Line Item</div>
                <div className='text-right'>{currentFiling.periods.col1}</div>
                <div className='text-right'>{currentFiling.periods.col2}</div>
                <div className='text-right'>YoY</div>
              </div>
            ) : (
              // 10-Q: 3 data columns + QoQ + YoY
              <div className='sticky top-0 z-10 bg-surface border-b border-edge px-2 py-1.5'>
                <div className='grid grid-cols-[1fr_68px_68px_68px_50px_50px] gap-x-1.5 text-[10px] font-semibold text-ink-muted'>
                  <div>Line Item</div>
                  <div className='text-right'>{currentFiling.periods.col1}</div>
                  <div className='text-right'>{currentFiling.periods.col2}</div>
                  <div className='text-right'>{currentFiling.periods.col3}</div>
                  <div className='text-right'>QoQ</div>
                  <div className='text-right'>YoY</div>
                </div>
                {currentFiling.quarterEnded && (
                  <div className='grid grid-cols-[1fr_68px_68px_68px_50px_50px] gap-x-1.5 text-[9px] font-normal italic text-ink-muted mt-0.5'>
                    <div>Quarter ended</div>
                    <div className='text-right'>
                      {currentFiling.quarterEnded.col1}
                    </div>
                    <div className='text-right'>
                      {currentFiling.quarterEnded.col2}
                    </div>
                    <div className='text-right'>
                      {currentFiling.quarterEnded.col3}
                    </div>
                    <div />
                    <div />
                  </div>
                )}
              </div>
            )}

            {/* Data rows - different layout for 10-K vs 10-Q */}
            {currentFiling.rows.map((row, idx) => {
              const col1: string = row.col1;
              const col2: string = row.col2;
              const col3: string = row.col3;
              const isActive = currentXbrlTag === row.xbrlTag && !!row.xbrlTag;
              const isEven = idx % 2 === 0;
              const is10K = currentFiling.filingType === '10-K';

              // Resolve effective period end dates (row overrides take priority)
              const endCol1 =
                row.periodEndDateOverrides?.col1 ??
                currentFiling.periodEndDates.col1;
              const endCol2 =
                row.periodEndDateOverrides?.col2 ??
                currentFiling.periodEndDates.col2;
              const endCol3 =
                row.periodEndDateOverrides?.col3 ??
                currentFiling.periodEndDates.col3;

              if (row.isHeader) {
                const asReportedSections = [
                  'Income Statement',
                  'Balance Sheet',
                  'Cash Flow',
                ];
                const showAsReported = asReportedSections.includes(row.label);
                return (
                  <div
                    key={idx}
                    className='px-2 pt-2 pb-1 text-[10px] font-bold text-navy uppercase tracking-wider bg-white'
                    style={{
                      borderTop: idx > 0 ? `1px solid ${colors.edge}` : 'none',
                    }}
                  >
                    {row.label}
                    {showAsReported && (
                      <span className='text-[9px] font-normal text-ink-muted normal-case tracking-normal ml-1'>
                        (as reported)
                      </span>
                    )}
                  </div>
                );
              }

              if (is10K) {
                return (
                  <div
                    key={idx}
                    className='grid grid-cols-[1fr_80px_80px_58px] gap-x-2 px-2 py-1'
                    style={{
                      background: isActive
                        ? `${colors.accent}15`
                        : isEven
                          ? '#fff'
                          : colors.surfaceAlt,
                      borderLeft: isActive
                        ? `2px solid ${colors.accent}`
                        : '2px solid transparent',
                    }}
                    onMouseEnter={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = `${colors.accent}08`;
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        e.currentTarget.style.background = isEven
                          ? '#fff'
                          : colors.surfaceAlt;
                      }
                    }}
                  >
                    <div className='text-[11px] text-ink-secondary font-normal truncate'>
                      {row.label}
                    </div>
                    <div
                      className='font-mono text-right text-[11px] font-normal text-navy'
                      style={{
                        textDecoration: isLinkable(col1, row.xbrlTag, 'col1')
                          ? 'underline'
                          : 'none',
                        textDecorationColor: `${colors.navy}40`,
                        cursor: isLinkable(col1, row.xbrlTag, 'col1')
                          ? 'pointer'
                          : 'default',
                      }}
                      onClick={() => {
                        if (isLinkable(col1, row.xbrlTag, 'col1')) {
                          handleCellClick(row.xbrlTag!, endCol1, 'col1');
                        }
                      }}
                    >
                      {col1}
                    </div>
                    <div
                      className='font-mono text-right text-[11px] text-navy'
                      style={{
                        textDecoration: isLinkable(col2, row.xbrlTag, 'col2')
                          ? 'underline'
                          : 'none',
                        textDecorationColor: `${colors.navy}40`,
                        cursor: isLinkable(col2, row.xbrlTag, 'col2')
                          ? 'pointer'
                          : 'default',
                      }}
                      onClick={() => {
                        if (isLinkable(col2, row.xbrlTag, 'col2')) {
                          handleCellClick(row.xbrlTag!, endCol2, 'col2');
                        }
                      }}
                    >
                      {col2}
                    </div>
                    <div className='text-right'>
                      {formatChange(row.yoyChange)}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={idx}
                  className='grid grid-cols-[1fr_68px_68px_68px_50px_50px] gap-x-1.5 px-2 py-1'
                  style={{
                    background: isActive
                      ? `${colors.accent}15`
                      : isEven
                        ? '#fff'
                        : colors.surfaceAlt,
                    borderLeft: isActive
                      ? `2px solid ${colors.accent}`
                      : '2px solid transparent',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = `${colors.accent}08`;
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = isEven
                        ? '#fff'
                        : colors.surfaceAlt;
                    }
                  }}
                >
                  <div className='text-[11px] text-ink-secondary font-normal truncate'>
                    {row.label}
                  </div>
                  <div
                    className='font-mono text-right text-[11px] font-normal text-navy'
                    style={{
                      textDecoration: isLinkable(col1, row.xbrlTag, 'col1')
                        ? 'underline'
                        : 'none',
                      textDecorationColor: `${colors.navy}40`,
                      cursor: isLinkable(col1, row.xbrlTag, 'col1')
                        ? 'pointer'
                        : 'default',
                    }}
                    onClick={() => {
                      if (isLinkable(col1, row.xbrlTag, 'col1')) {
                        handleCellClick(row.xbrlTag!, endCol1, 'col1');
                      }
                    }}
                  >
                    {col1}
                  </div>
                  <div
                    className='font-mono text-right text-[11px] text-navy'
                    style={{
                      textDecoration: isLinkable(col2, row.xbrlTag, 'col2')
                        ? 'underline'
                        : 'none',
                      textDecorationColor: `${colors.navy}40`,
                      cursor: isLinkable(col2, row.xbrlTag, 'col2')
                        ? 'pointer'
                        : 'default',
                    }}
                    onClick={() => {
                      if (isLinkable(col2, row.xbrlTag, 'col2')) {
                        handleCellClick(row.xbrlTag!, endCol2, 'col2');
                      }
                    }}
                  >
                    {col2}
                  </div>
                  <div
                    className='font-mono text-right text-[11px] text-navy'
                    style={{
                      textDecoration: isLinkable(col3, row.xbrlTag, 'col3')
                        ? 'underline'
                        : 'none',
                      textDecorationColor: `${colors.navy}40`,
                      cursor: isLinkable(col3, row.xbrlTag, 'col3')
                        ? 'pointer'
                        : 'default',
                    }}
                    onClick={() => {
                      if (isLinkable(col3, row.xbrlTag, 'col3')) {
                        handleCellClick(row.xbrlTag!, endCol3, 'col3');
                      }
                    }}
                  >
                    {col3}
                  </div>
                  <div className='text-right'>
                    {formatChange(row.qoqChange ?? null)}
                  </div>
                  <div className='text-right'>
                    {formatChange(row.yoyChange)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className='flex items-center justify-center h-full text-ink-muted text-xs'>
            <div className='text-center p-4'>
              <div className='w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 bg-surface'>
                <svg
                  className='w-4 h-4'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={1.5}
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z'
                  />
                </svg>
              </div>
              <p className='font-medium text-[11px]'>Coming Soon</p>
            </div>
          </div>
        )}
      </div>

      <MarketDataFooter
        liveData={liveData}
        sharesOutstanding={companyData.sharesOutstanding}
        netDebt={getNetDebt()}
      />
    </div>
  );
}
